import { useTranslation } from "@next-platform/client-common-core";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { Modal } from "@next-platform/client-common-spicy-ui";
import { type JSX, useState } from "react";

import { Col, Row, SpicyButton, Text } from "@/components/customComponents";

const logger = appLogger.withTag("ConfirmationModal");

type ConfirmationModalProps = {
  heading: string;
  handleClose: () => void;
  onUserConfirmation?: () => Promise<void | { error: string }>;
  confirmationMessage: string | JSX.Element;
  isVisible: boolean;
  isLoading: boolean;
  actionButtonColor?: string;
  actionButtonText?: string;
  discardButtonText?: string;
  showButtons?: boolean;
  link?: string;
  linkText?: string;
  screen?: string;
  handleOnClose?: () => void;
  titleClassNames?: string;
  dataTestId?: string;
};

function ConfirmationModal({
  heading,
  onUserConfirmation,
  link = "",
  linkText = "",
  isLoading = false,
  handleClose,
  handleOnClose,
  confirmationMessage = "",
  isVisible,
  showButtons = true,
  actionButtonColor = "primary_strong",
  actionButtonText,
  discardButtonText,
  screen = "",
  titleClassNames = "",
  dataTestId = "",
}: ConfirmationModalProps) {
  const { t } = useTranslation();
  const [error, setStateError] = useState("");
  const onUserConfirmationClickHandler = async () => {
    try {
      setStateError("");
      const res = await onUserConfirmation?.();

      if (res?.error) {
        return setStateError(res?.error);
      }
      if (!error && screen !== "UserProfile" && screen !== "CreateOrEditCharacter") {
        handleClose();
      }
      // backward compatible
    } catch (error) {
      logger.error("Error in onUserConfirmationClickHandler", error);
      const err = error as { message?: string };
      setStateError(err.message ?? t("error:fallbackError"));
    }
  };

  const onDismiss = () => {
    if (handleOnClose) {
      handleOnClose();
    } else {
      handleClose?.();
    }
    setStateError("");
  };

  const onDiscard = () => {
    handleClose?.();
    setStateError("");
  };

  return (
    <Modal
      title={heading}
      classNames={{ title: `text-center pl-[28px] ${titleClassNames}` }}
      dismissable={!isLoading}
      aria-labelledby="confirmation modal"
      open={isVisible}
      onOpenChange={onDismiss}
    >
      <Col className="text-center" dataTestId={dataTestId}>
        <Text variant={"md"} type={"normal"}>
          {confirmationMessage}{" "}
          {link && linkText ? (
            <a className="text-link no-underline" href={link}>
              {linkText}
            </a>
          ) : null}
        </Text>
        {error ? (
          <Text variant={"xs"} type={"error"}>
            {error}
          </Text>
        ) : null}
      </Col>
      {showButtons ? (
        <Row className="justify-center gap-2">
          <SpicyButton isDisabled={isLoading} variant="primary_large_bordered" color="transparent" onClick={onDiscard}>
            {discardButtonText || t("common:no")}
          </SpicyButton>

          <SpicyButton
            isLoading={isLoading}
            variant="primary_large"
            // @ts-expect-error we need to create type for this
            color={actionButtonColor}
            onClick={onUserConfirmationClickHandler}
          >
            {actionButtonText || t("common:yes")}
          </SpicyButton>
        </Row>
      ) : null}
    </Modal>
  );
}

export default ConfirmationModal;
